import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// project imports
import IconButtonExt from 'components/@extended/IconButton';
import useAuth from 'hooks/useAuth';
import {
  setSharedClockState,
  refreshSharedClockState,
  useSharedClockState,
  setSharedOpenPropustka,
  refreshSharedOpenPropustka,
  useSharedOpenPropustka
} from 'hooks/useAttendanceClockState';
import { notifyAttendanceChanged } from 'hooks/useAttendanceRefresh';
import useExclusiveAccordion from 'utils/useExclusiveAccordion';
import { formatDateDisplay } from 'utils/formatDate';
import { hasModuleAccess } from 'utils/roleWeights';
import { propustkaReasonIconFor } from 'utils/propustkaReasonIcons';
import { verificationReasonIconFor } from 'utils/verificationReasonIcons';
import { requiredFieldSx } from 'utils/requiredFieldSx';
import {
  clockAttendance,
  closeOpenLeavePeriod,
  createPropustka,
  endBreak,
  getAttendanceState,
  getBreakState,
  getMyLeaveEntitlement,
  getVerificationReasons,
  listMyLeavePeriods,
  listMyPropustkaRequests,
  listPropustkaReasons,
  markPropustkaReturned,
  recordInformationalLeave,
  requestLeavePeriod,
  startBreak
} from 'api/attendance';

// assets
import {
  IconBreak,
  IconCalendar,
  IconClose,
  IconExpandMore,
  IconFactCheck,
  IconFamilyCare,
  IconHistoryToggle,
  IconPropustka,
  IconVacationRequest,
  IconWarningTriangle,
  IconWorkHistory
} from 'utils/icons';

// ==============================|| PROFILE - DOCHÁZKA A ŽÁDOSTI (ACCORDION) ||============================== //
// Zaměstnanecká sebeobsluha v offcanvas-right-user (2026-08-23, na výslovné
// přání uživatele) - stejné akce jako na /attendance a
// /attendance/leave-requests, jen kompaktní verze přímo v profilové
// nabídce pro rychlý přístup ("zachovat obojí" - obě cesty existují
// vedle sebe, plné stránky zůstávají). Vlastní, samostatný stav (ne sdílený
// s těmi stránkami) - vědomá duplicita na výslovné přání uživatele.
//
// Vykresluje se jen s rolí na module_key 'attendance' (stejná brána jako
// route guard na plných stránkách) - viz Profile/index.jsx.
//
// 2026-08-29, na výslovné přání uživatele ("modal buď s úspěchem nebo bez
// úspěchu s doplněním, to platí u všech tlačítek u docházky, vše bude pak
// dokončeno v modal") - kompletní přestavba ze 4 inline rozbalovacích
// SubAccordion na 4 klikací řádky, každý otevře DIALOG. Uvnitř dialogu se
// pak (podle akce) buď rovnou zkusí akci provést (Odchod nebo příchod - viz
// `openClockModal`, důvod se dořeší přímo v dialogu, když je potřeba), nebo
// se v dialogu vyplní formulář a odešle (Propustka/Dovolená/OČR) - výsledek
// (úspěch i chyba) se vždy zobrazí uvnitř TOHOTO SAMÉHO dialogu, ne inline v
// akordeonu jako dřív. Existující stav/handlery (clockState, handleClock,
// handleCreatePropustka, ...) beze změny v logice, jen přesunuté z
// AccordionDetails do DialogContent.

function tryGetPosition(timeoutMs = 4000) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    const timer = setTimeout(() => resolve(null), timeoutMs);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(timer);
        resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
      },
      () => {
        clearTimeout(timer);
        resolve(null);
      },
      { timeout: timeoutMs }
    );
  });
}

// 2026-08-29, na výslovné přání uživatele ("v docházce nechat ty
// accordiony") - vrátilo se zpět na rozbalovací SubAccordion (dřív krátce
// nahrazeno plochým klikacím řádkem, co rovnou otevíral modal). Rozbalené
// tělo teď ale obsahuje jen stručný stav + upozornění + JEDNO tlačítko,
// které teprve otevře modal (ActionDialog níže) - samotný formulář/výsledek
// zůstává v modalu, akordeon je jen "vstupní brána" s varovným textem.
function SubAccordion({ label, icon: Icon, iconColor, expanded, onChange, children }) {
  return (
    <Accordion disableGutters elevation={0} square expanded={expanded} onChange={onChange} sx={{ '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<IconExpandMore style={{ fontSize: '0.9rem' }} />} sx={{ px: 2.5, minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.75 } }}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          {Icon ? (
            <Icon color={iconColor} style={{ fontSize: '1.3rem', flexShrink: 0 }} />
          ) : (
            <Box sx={{ width: '1.3rem', flexShrink: 0 }} />
          )}
          <Typography variant="body2">{label}</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2.5, pt: 1.25, pb: 1.5, borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider' }}>
        {/* textAlign: 'center' (2026-08-29, na výslovné přání uživatele -
            "texty v accordion docházce budou na střed") - dědí se na
            všechny textové děti, tlačítka (fullWidth) tím nejsou dotčená. */}
        <Stack sx={{ gap: 1.25, textAlign: 'center' }}>{children}</Stack>
      </AccordionDetails>
    </Accordion>
  );
}

// Přepočet dny <-> hodiny u žádosti o dovolenou (2026-09-02, na výslovné
// přání uživatele - "pro lepší čitelnost by mohlo být i pole na dny, ať
// zadá jeden nebo druhý, automaticky se přepočítá") - čistě vizuální
// pomůcka, appka VŽDY ukládá jen hodiny (viz leaveHours). Stejná hodnota
// jako AttendanceLeavePeriodService::VACATION_HOURS_PER_WORKDAY na
// backendu - appka ji ale i tak validuje/rozkládá znovu server-side podle
// skutečných pracovních dní (svátky/víkendy), tenhle přepočet je jen
// orientační odhad pro zadávání, ne autoritativní hodnota.
const VACATION_HOURS_PER_DAY = 8;

function round2(value) {
  return Math.round(value * 100) / 100;
}

function ActionDialog({ open, title, onClose, children }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {title}
        <IconButtonExt size="small" onClick={onClose} aria-label="zavřít">
          <IconClose style={{ fontSize: '1.3rem' }} />
        </IconButtonExt>
      </DialogTitle>
      <DialogContent>
        <Stack sx={{ gap: 1.25, pt: 0.5 }}>{children}</Stack>
      </DialogContent>
    </Dialog>
  );
}

export default function AttendanceAccordion({ onNavigate, expanded: sectionExpanded, onChange: onSectionChange }) {
  const { user } = useAuth();
  const canSeeAttendance = hasModuleAccess(user?.permissions, 'attendance', 'reader');
  const navigate = useNavigate();

  const goTo = (path) => {
    onNavigate?.();
    navigate(path);
  };

  const [expanded, handleAccordionChange] = useExclusiveAccordion();

  // null | 'clock' | 'propustka' | 'leave' | 'ocr'
  const [openModal, setOpenModal] = useState(null);
  const closeModal = () => setOpenModal(null);

  // Odchod nebo příchod - sdílený "external store" s Profile/index.jsx's
  // rychlým tlačítkem v hlavičce (2026-08-29, real bug fixed), viz
  // hooks/useAttendanceClockState.js's docblock.
  const clockState = useSharedClockState();
  const [clockBusy, setClockBusy] = useState(false);
  const [clockError, setClockError] = useState('');
  const [clockSuccess, setClockSuccess] = useState('');
  const [needsReason, setNeedsReason] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [selectedReasonIds, setSelectedReasonIds] = useState([]);
  // "Zapomněl jsem se odhlásit" (2026-09-04, na výslovné přání uživatele) -
  // jen u odchodu, appka pak čas odchodu neposílá jako "teď", ale jako
  // dodatečně zadaný - viz AttendanceEventService::clock()'s docblock.
  // Vždy se tím označí needs_review, vedoucí/mzdová to potvrdí v Uzávěrce
  // docházky - appka nic neblokuje.
  const [forgotClockOut, setForgotClockOut] = useState(false);
  const [forgotClockOutTime, setForgotClockOutTime] = useState('');

  // Propustka - openPropustka sdílený "external store" s Profile/index.jsx
  // (2026-08-31, na výslovné přání uživatele - "PROBÍHÁ PROPUSTKA" vedle
  // fotky), stejný vzor jako clockState výše.
  const [propustkaReasons, setPropustkaReasons] = useState([]);
  // Schválené, ještě nenapojené žádanky (2026-09-03, na výslovné přání
  // uživatele - "dá se při nové žádosti propustky v modálu mít schválené
  // propustky a aktivovat je tlačítkem?") - nabízí je rovnou v modálu
  // Propustky místo spoléhání na tiché napojení podle
  // AttendancePropustkaService::create() (viz handleActivateRequest níže).
  const [myPropustkaRequests, setMyPropustkaRequests] = useState([]);
  const openPropustka = useSharedOpenPropustka();
  const [propustkaReasonId, setPropustkaReasonId] = useState('');
  const [propustkaDate, setPropustkaDate] = useState('');
  // "Nevím ještě, jestli půjde o propustku nebo nemoc" (2026-09-01,
  // Technická dokumentace bod 5 - "neplánovaná zdravotní absence") -
  // zapíná pending_classification, nadřízený/mzdová to pak musí doložit
  // (potvrdit jako propustku, nebo překlasifikovat na pracovní neschopnost)
  // přes AttendancePropustkaService::resolvePendingClassification().
  const [propustkaPending, setPropustkaPending] = useState(false);
  const [propustkaBusy, setPropustkaBusy] = useState(false);
  const [propustkaError, setPropustkaError] = useState('');
  const [propustkaSuccess, setPropustkaSuccess] = useState('');
  const [returnTime, setReturnTime] = useState('');
  const [returnBusy, setReturnBusy] = useState(false);

  // Přestávka (2026-09-02) - self-service evidence, server-time only (viz
  // AttendanceBreakService's docblock). Lokální stav (na rozdíl od
  // propustky) - jednoduchý toggle, žádný sdílený "external store" jinde v
  // appce zatím není potřeba.
  const [openBreak, setOpenBreak] = useState(null);
  const [breakBusy, setBreakBusy] = useState(false);
  const [breakError, setBreakError] = useState('');

  // Žádost o dovolenou
  const [leaveType, setLeaveType] = useState('vacation');
  const [leaveStart, setLeaveStart] = useState('');
  const [leaveEnd, setLeaveEnd] = useState('');
  // leaveHours (2026-09-02, na výslovné přání uživatele - "u dovolené
  // nejsou denní či půldenní, musí to být vše v hodinách") - appka nemá
  // zvlášť koncept celého/půl dne, jen počet hodin za celý rozsah od-do;
  // POVINNÉ jen pro typ Dovolená, u Neplaceného volna se neposílá (appka
  // tam dál zůstává u dřívějšího paušálu, žádné saldo se netrackuje).
  // leaveDays (2026-09-02, na výslovné přání uživatele - "pro lepší
  // čitelnost a propočet by v žádosti mohl být i pole na zadání dnů, ať
  // zadá jeden nebo druhý, automaticky se to přepočítá do toho druhého") -
  // čistě VIZUÁLNÍ pomocné pole, appka ukládá VŽDY jen leaveHours - dny
  // jsou jen přepočet přes VACATION_HOURS_PER_DAY, žádný vlastní backend
  // koncept.
  const [leaveHours, setLeaveHours] = useState('');
  const [leaveDays, setLeaveDays] = useState('');
  const [leaveBusy, setLeaveBusy] = useState(false);
  const [leaveError, setLeaveError] = useState('');
  const [leaveSuccess, setLeaveSuccess] = useState('');
  // Zbývající nárok v hodinách (2026-08-30, na výslovné přání uživatele -
  // "do závorky dej číslo v hodinách kolik uživateli zbývá") - undefined =
  // ještě nenačteno/nenastaveno, do závorky v labelu SubAccordionu se pak
  // vůbec nepíše, viz label={} níže.
  const [remainingVacationHours, setRemainingVacationHours] = useState(undefined);

  // OČR
  const [openOcr, setOpenOcr] = useState(null);
  const [ocrStart, setOcrStart] = useState('');
  const [ocrCareCode, setOcrCareCode] = useState('');
  const [ocrEndDate, setOcrEndDate] = useState('');
  const [ocrEndCareCode, setOcrEndCareCode] = useState('');
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrError, setOcrError] = useState('');
  const [ocrSuccess, setOcrSuccess] = useState('');

  useEffect(() => {
    if (!canSeeAttendance) return;
    refreshSharedClockState();
    listPropustkaReasons()
      .then(setPropustkaReasons)
      // Byl tu tichý catch(() => {}) - selhání načtení (401/500/...) vypadalo
      // úplně stejně jako "žádné důvody nejsou nastavené", takže se to
      // nedalo od sebe rozeznat (2026-08-31, uživatelský report "opět ty
      // důvody nejsou"). Teď je vidět v dialogu Propustky, viz propustkaError.
      .catch((e) => setPropustkaError(e.message || 'Důvody propustky se nepodařilo načíst.'));
    loadMyPropustkaRequests();
    refreshSharedOpenPropustka();
    getBreakState()
      .then(setOpenBreak)
      .catch(() => {});
    listMyLeavePeriods()
      .then((rows) => setOpenOcr(rows.find((l) => l.leave_type === 'family_care' && !l.end_date) ?? null))
      .catch(() => {});
    getMyLeaveEntitlement(new Date().getFullYear())
      .then((data) => setRemainingVacationHours(data.remaining_hours))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSeeAttendance]);

  const handleClock = async (reasonIds, occurredAtTime) => {
    setClockBusy(true);
    setClockError('');
    setClockSuccess('');
    try {
      const position = await tryGetPosition();
      let occurredAt;
      if (occurredAtTime) {
        const now = new Date();
        const [hh, mm] = occurredAtTime.split(':');
        occurredAt = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:00`;
      }
      await clockAttendance('standard', undefined, {
        latitude: position?.latitude,
        longitude: position?.longitude,
        verification_reason_ids: reasonIds && reasonIds.length > 0 ? reasonIds : undefined,
        occurred_at: occurredAt
      });
      const d = await getAttendanceState();
      setSharedClockState(d.state);
      setClockSuccess('Zaznamenáno.');
      setNeedsReason(false);
      setSelectedReasonIds([]);
      setForgotClockOut(false);
      setForgotClockOutTime('');
      notifyAttendanceChanged();
    } catch (e) {
      if (e.code === 'ATT-400-001') {
        try {
          const fresh = await getAttendanceState();
          setSharedClockState(fresh.state);
          const freshNextAction = fresh.state === 'clock_in' ? 'clock_out' : 'clock_in';
          setReasons(await getVerificationReasons(freshNextAction));
        } catch {
          setReasons([]);
        }
        setNeedsReason(true);
      } else {
        setClockError(e.message || 'Píchnutí se nepodařilo zaznamenat.');
      }
    } finally {
      setClockBusy(false);
    }
  };

  const openClockModal = () => {
    setClockError('');
    setClockSuccess('');
    setNeedsReason(false);
    setSelectedReasonIds([]);
    setForgotClockOut(false);
    setForgotClockOutTime('');
    setOpenModal('clock');
    // Odchod se needs_review-vat neptá rovnou (může jít o dodatečně zadaný
    // čas, viz níže v dialogu) - příchod se pořád zaznamenává hned, jako
    // dřív.
    if (clockState !== 'clock_in') {
      handleClock([]);
    }
  };

  const toggleReason = (id) => {
    setSelectedReasonIds((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));
  };

  const loadMyPropustkaRequests = () => listMyPropustkaRequests().then(setMyPropustkaRequests).catch(() => {});

  const handleActivateRequest = async (request) => {
    setPropustkaBusy(true);
    setPropustkaError('');
    setPropustkaSuccess('');
    try {
      const id = await createPropustka(
        request.reason_id,
        request.requested_date,
        request.requested_departure_time,
        request.requested_return_time,
        request.comment,
        false
      ).then((r) => r.id);
      setSharedOpenPropustka({ id, reason_label: request.reason_label, propustka_date: request.requested_date, return_time: null });
      setPropustkaSuccess('Žádanka byla aktivována do docházky.');
      loadMyPropustkaRequests();
      notifyAttendanceChanged();
    } catch (e) {
      setPropustkaError(e.message || 'Žádanku se nepodařilo aktivovat.');
    } finally {
      setPropustkaBusy(false);
    }
  };

  const handleCreatePropustka = async () => {
    if (!propustkaReasonId || !propustkaDate) return;
    setPropustkaBusy(true);
    setPropustkaError('');
    setPropustkaSuccess('');
    try {
      const id = await createPropustka(propustkaReasonId, propustkaDate, undefined, undefined, undefined, propustkaPending).then((r) => r.id);
      const reason = propustkaReasons.find((r) => String(r.id) === String(propustkaReasonId));
      setSharedOpenPropustka({ id, reason_label: reason?.label, propustka_date: propustkaDate, return_time: null });
      setPropustkaSuccess('Propustka byla zapsána.');
      setPropustkaReasonId('');
      setPropustkaDate('');
      setPropustkaPending(false);
      loadMyPropustkaRequests();
      notifyAttendanceChanged();
    } catch (e) {
      setPropustkaError(e.message || 'Propustku se nepodařilo zapsat.');
    } finally {
      setPropustkaBusy(false);
    }
  };

  const handleMarkReturned = async () => {
    if (!returnTime) return;
    setReturnBusy(true);
    setPropustkaError('');
    setPropustkaSuccess('');
    try {
      await markPropustkaReturned(returnTime);
      setSharedOpenPropustka(null);
      setPropustkaSuccess('Návrat byl zaznamenán.');
      setReturnTime('');
      notifyAttendanceChanged();
    } catch (e) {
      setPropustkaError(e.message || 'Návrat se nepodařilo zaznamenat.');
    } finally {
      setReturnBusy(false);
    }
  };

  const openPropustkaModal = () => {
    setPropustkaError('');
    setPropustkaSuccess('');
    setOpenModal('propustka');
  };

  const handleToggleBreak = async () => {
    setBreakBusy(true);
    setBreakError('');
    try {
      if (openBreak) {
        await endBreak();
        setOpenBreak(null);
      } else {
        const { id } = await startBreak();
        setOpenBreak({ id, start_time: new Date().toTimeString().slice(0, 8) });
      }
      notifyAttendanceChanged();
    } catch (e) {
      setBreakError(e.message || 'Přestávku se nepodařilo zaznamenat.');
    } finally {
      setBreakBusy(false);
    }
  };

  const handleLeaveHoursChange = (value) => {
    setLeaveHours(value);
    setLeaveDays(value !== '' && !Number.isNaN(Number(value)) ? String(round2(Number(value) / VACATION_HOURS_PER_DAY)) : '');
  };

  const handleLeaveDaysChange = (value) => {
    setLeaveDays(value);
    setLeaveHours(value !== '' && !Number.isNaN(Number(value)) ? String(round2(Number(value) * VACATION_HOURS_PER_DAY)) : '');
  };

  const handleRequestLeave = async () => {
    if (!leaveStart || !leaveEnd) return;
    if (leaveType === 'vacation' && !leaveHours) return;
    setLeaveBusy(true);
    setLeaveError('');
    setLeaveSuccess('');
    try {
      await requestLeavePeriod(leaveType, leaveStart, leaveEnd, undefined, leaveType === 'vacation' ? leaveHours : undefined);
      setLeaveSuccess('Žádost byla podána.');
      setLeaveStart('');
      setLeaveEnd('');
      setLeaveHours('');
      setLeaveDays('');
      notifyAttendanceChanged();
    } catch (e) {
      setLeaveError(e.message || 'Žádost se nepodařilo podat.');
    } finally {
      setLeaveBusy(false);
    }
  };

  const openLeaveModal = () => {
    setLeaveError('');
    setLeaveSuccess('');
    setOpenModal('leave');
  };

  const handleRecordOcr = async () => {
    if (!ocrStart || !ocrCareCode.trim()) return;
    setOcrBusy(true);
    setOcrError('');
    setOcrSuccess('');
    try {
      await recordInformationalLeave('family_care', ocrStart, null, undefined, ocrCareCode.trim());
      setOpenOcr({ start_date: ocrStart });
      setOcrSuccess('Záznam byl uložen.');
      setOcrStart('');
      setOcrCareCode('');
      notifyAttendanceChanged();
    } catch (e) {
      setOcrError(e.message || 'Záznam se nepodařilo uložit.');
    } finally {
      setOcrBusy(false);
    }
  };

  const handleCloseOcr = async () => {
    if (!ocrEndDate || !ocrEndCareCode.trim()) return;
    setOcrBusy(true);
    setOcrError('');
    setOcrSuccess('');
    try {
      await closeOpenLeavePeriod(ocrEndDate, ocrEndCareCode.trim());
      setOpenOcr(null);
      setOcrSuccess('OČR bylo ukončeno.');
      setOcrEndDate('');
      setOcrEndCareCode('');
      notifyAttendanceChanged();
    } catch (e) {
      setOcrError(e.message || 'Ukončení se nepodařilo.');
    } finally {
      setOcrBusy(false);
    }
  };

  const openOcrModal = () => {
    setOcrError('');
    setOcrSuccess('');
    setOpenModal('ocr');
  };

  const nextClockAction = clockState === 'clock_in' ? 'clock_out' : 'clock_in';

  // Propustka info-modal (2026-08-31, na výslovné přání uživatele, jen
  // lokální experiment) - text tlačítka závisí na kombinaci "je propustka
  // už otevřená" x "je uživatel právě v práci" (clockState === 'clock_in').
  // Akce tlačítka zůstává stejná jako doteď (otevře formulářový modal,
  // openPropustkaModal) - mění se jen návodný text, ne co appka reálně
  // udělá po kliknutí.
  const isWorking = clockState === 'clock_in';
  const propustkaActionLabel = !openPropustka
    ? isWorking
      ? 'Přerušit pracovní dobu a začít propustku'
      : 'Začít propustku od začátku pracovní doby'
    : isWorking
      ? 'Ukončit propustku a pokračovat v pracovní době'
      : 'Ukončení propustky koncem pracovní doby';

  if (!canSeeAttendance) return null;

  return (
    <>
      <Accordion
        disableGutters
        elevation={0}
        square
        expanded={sectionExpanded}
        onChange={onSectionChange}
        sx={{ '&:before': { display: 'none' }, borderBottom: 1, borderColor: 'divider' }}
      >
        <AccordionSummary expandIcon={<IconExpandMore style={{ fontSize: '1.05rem' }} />} sx={{ px: 2.5 }}>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
            <IconCalendar style={{ fontSize: '1.3rem' }} />
            <Typography variant="subtitle1">Evidence docházky</Typography>
          </Stack>
        </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <SubAccordion
          label={clockState === 'clock_in' ? 'Ukončit práci' : 'Zahájit práci'}
          icon={IconHistoryToggle}
          iconColor={clockState === 'clock_in' ? 'success' : 'error'}
          expanded={expanded === 'clock'}
          onChange={(event, isExpanded) => {
            handleAccordionChange('clock')(event, isExpanded);
            // `clockState` je teď sdílený store (viz import výše), takže se
            // sem propíše i píchnutí přes hlavičkové tlačítko automaticky -
            // tenhle refresh při otevření je jen dodatečná pojistka
            // čerstvosti (kdyby appka běžela dlouho na pozadí).
            if (isExpanded) refreshSharedClockState();
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: clockState === 'clock_in' ? 'success.main' : clockState === undefined || clockState === null ? 'text.secondary' : 'error.main' }}
          >
            {clockState === undefined ? '…' : clockState === null ? 'Zatím jste dnes nepíchli.' : clockState === 'clock_in' ? 'Aktuálně: přítomen' : 'Aktuálně: nepřítomen'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Pro zaměstnance pracující na pobočce je stanovená základní (stálá) pracovní doba od 7:00 do 15:45, v tomto časovém úseku
            je přítomnost na pracovišti povinná pro všechny pracovníky pobočky.
          </Typography>
          <Button
            fullWidth
            size="small"
            variant="contained"
            color={nextClockAction === 'clock_in' ? 'success' : 'error'}
            onClick={openClockModal}
            sx={{ textTransform: 'none' }}
          >
            {nextClockAction === 'clock_in' ? 'Příchod' : 'Odchod'}
          </Button>
        </SubAccordion>

        <ListItemButton
          onClick={() => setOpenModal('break')}
          sx={{ px: 2.5, minHeight: 40, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <IconBreak style={{ fontSize: '1.3rem', color: openBreak ? 'var(--mui-palette-warning-main)' : undefined }} />
          </ListItemIcon>
          <ListItemText
            primary={openBreak ? 'Přestávka probíhá' : 'Přestávka'}
            slotProps={{ primary: { variant: 'body2', sx: { color: openBreak ? 'warning.main' : undefined } } }}
          />
        </ListItemButton>

        {/* EXPERIMENT (2026-08-31, na výslovné přání uživatele, jen lokálně
            vyzkoušet) - "Propustka" tu dřív (do 2026-08-29) bývala plochý
            klikací řádek rovnou do modalu, pak se vrátila zpět na
            rozbalovací SubAccordion na výslovné přání ("nechat ty
            accordiony"). Tohle je návrat k plochému řádku, ale s mezikrokem
            navíc: řádek otevře INFO modal (obsah = to, co bývalo v těle
            accordionu), a teprve jeho tlačítko otevře druhý, formulářový
            modal (stejný ActionDialog open={openModal === 'propustka'}
            níže, beze změny). Stejný postup (2026-08-31) i u "Ošetřování
            člena rodiny" a "Žádost o dovolenou / n. volno" níže. "Odchod
            nebo příchod" zůstává beze změny (accordion). */}
        <ListItemButton
          onClick={() => setOpenModal('propustka-info')}
          sx={{ px: 2.5, minHeight: 40, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <IconPropustka style={{ fontSize: '1.3rem' }} />
          </ListItemIcon>
          <ListItemText primary="Propustka" slotProps={{ primary: { variant: 'body2' } }} />
        </ListItemButton>

        <ListItemButton
          onClick={() => setOpenModal('leave-info')}
          sx={{ px: 2.5, minHeight: 40, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <IconVacationRequest style={{ fontSize: '1.3rem' }} />
          </ListItemIcon>
          <ListItemText primary="Žádost o dovolenou / n. volno" slotProps={{ primary: { variant: 'body2' } }} />
        </ListItemButton>

        <ListItemButton
          onClick={() => setOpenModal('ocr-info')}
          sx={{ px: 2.5, minHeight: 40, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <IconFamilyCare style={{ fontSize: '1.3rem' }} />
          </ListItemIcon>
          <ListItemText primary="Ošetřování člena rodiny" slotProps={{ primary: { variant: 'body2' } }} />
        </ListItemButton>
      </AccordionDetails>

      <ActionDialog open={openModal === 'clock'} title="Odchod nebo příchod" onClose={closeModal}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {clockState === undefined ? '…' : clockState === null ? 'Zatím jste dnes nepíchli.' : clockState === 'clock_in' ? 'Aktuálně: přítomen' : 'Aktuálně: nepřítomen'}
        </Typography>

        {clockBusy && (
          <Stack sx={{ alignItems: 'center', py: 1 }}>
            <CircularProgress size={22} />
          </Stack>
        )}

        {!clockBusy && !needsReason && !clockSuccess && !clockError && clockState === 'clock_in' && (
          <Stack sx={{ gap: 1.25 }}>
            <Button
              fullWidth
              size="small"
              variant="contained"
              color="error"
              onClick={() => handleClock([])}
              sx={{ textTransform: 'none', py: '8px' }}
            >
              Odhlásit se nyní
            </Button>
            <FormControlLabel
              control={<Checkbox size="small" checked={forgotClockOut} onChange={(e) => setForgotClockOut(e.target.checked)} />}
              label={
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Zapomněl jsem se odhlásit dřív - chci zadat skutečný čas
                </Typography>
              }
            />
            {forgotClockOut && (
              <>
                <TextField
                  type="time"
                  label="Skutečný čas odchodu"
                  value={forgotClockOutTime}
                  onChange={(e) => setForgotClockOutTime(e.target.value)}
                  size="small"
                  fullWidth
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Záznam se označí ke kontrole vedoucímu/mzdové v Uzávěrce docházky.
                </Typography>
                <Button
                  fullWidth
                  size="small"
                  variant="contained"
                  disabled={!forgotClockOutTime}
                  onClick={() => handleClock([], forgotClockOutTime)}
                  sx={{ textTransform: 'none', py: '8px' }}
                >
                  Uložit s tímto časem
                </Button>
              </>
            )}
          </Stack>
        )}

        {!clockBusy && needsReason && (
          <Stack sx={{ gap: 1 }}>
            <Alert severity="warning">
              Nepodařilo se ověřit polohu ani firemní síť
              <br />
              vyberte alespoň jeden důvod.
            </Alert>
            {reasons.map((r) => {
              const ReasonIcon = verificationReasonIconFor(r.icon);
              return (
                <FormControlLabel
                  key={r.id}
                  control={<Checkbox size="small" checked={selectedReasonIds.includes(r.id)} onChange={() => toggleReason(r.id)} />}
                  label={
                    <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center' }}>
                      {ReasonIcon && <ReasonIcon style={{ fontSize: '1rem' }} />}
                      <Typography variant="caption">{r.label}</Typography>
                    </Stack>
                  }
                />
              );
            })}
            <Button
              fullWidth
              size="small"
              variant="contained"
              disabled={selectedReasonIds.length === 0}
              onClick={() => handleClock(selectedReasonIds, forgotClockOut ? forgotClockOutTime : undefined)}
              sx={{ textTransform: 'none', py: '8px' }}
            >
              Potvrdit a zaznamenat
            </Button>
          </Stack>
        )}

        {!clockBusy && !needsReason && clockSuccess && (
          <Stack sx={{ gap: 1.5 }}>
            <Typography variant="body2" sx={{ color: 'success.main' }}>
              {clockSuccess}
            </Typography>
            <Button fullWidth size="small" variant="outlined" onClick={closeModal} sx={{ textTransform: 'none', py: '8px' }}>
              Zavřít
            </Button>
          </Stack>
        )}

        {!clockBusy && !needsReason && clockError && (
          <Stack sx={{ gap: 1.5 }}>
            <Typography variant="body2" sx={{ color: 'error.main' }}>
              {clockError}
            </Typography>
            <Stack direction="row" sx={{ gap: 1 }}>
              <Button
                fullWidth
                size="small"
                variant="contained"
                onClick={() => handleClock([], forgotClockOut ? forgotClockOutTime : undefined)}
                sx={{ textTransform: 'none', py: '8px' }}
              >
                Zkusit znovu
              </Button>
              <Button fullWidth size="small" variant="outlined" onClick={closeModal} sx={{ textTransform: 'none', py: '8px' }}>
                Zavřít
              </Button>
            </Stack>
          </Stack>
        )}
      </ActionDialog>

      <ActionDialog open={openModal === 'break'} title="Přestávka" onClose={closeModal}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Zaznamenává skutečný začátek/konec vaší přestávky. Nezávislé na propustce - appka podle vašeho nastavení u Úvazku zaměstnance
          buď použije tenhle skutečný záznam, nebo ho nechá bez vlivu (pokud máte přestávku tolerovanou).
        </Typography>
        {breakError && <Alert severity="error">{breakError}</Alert>}
        <Typography variant="caption" sx={{ color: openBreak ? 'warning.main' : 'text.secondary' }}>
          {openBreak ? `Přestávka probíhá od ${String(openBreak.start_time).slice(0, 5)}` : 'Žádná probíhající přestávka.'}
        </Typography>
        <Button
          fullWidth
          size="small"
          variant="contained"
          color={openBreak ? 'warning' : 'primary'}
          disabled={breakBusy}
          onClick={handleToggleBreak}
          sx={{ textTransform: 'none', py: '8px' }}
        >
          {breakBusy ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : openBreak ? 'Ukončit přestávku' : 'Začít přestávku'}
        </Button>
      </ActionDialog>

      <ActionDialog open={openModal === 'propustka-info'} title="Začít / Ukončit propustku" onClose={closeModal}>
        {/* "Infotainment" rámeček (2026-08-31, na výslovné přání uživatele) -
            nativní fieldset/legend místo ruční přesahové pozice, dědí
            barvy/radius z MUI. Uvnitř jen stav (Žádná otevřená / Otevřená k
            ukončení), zbytek vysvětlujícího textu je v těle níže. */}
        <Box
          component="fieldset"
          sx={{ m: 0, px: 1.5, py: 1, border: '1px solid', borderColor: openPropustka ? 'warning.main' : 'divider', borderRadius: 1 }}
        >
          <Typography
            component="legend"
            variant="caption"
            sx={{ px: 0.5, fontSize: '0.5625rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            Infotainment
          </Typography>
          <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" sx={{ color: openPropustka ? 'warning.main' : 'text.secondary' }}>
              {openPropustka ? 'Otevřená k ukončení' : 'Žádná otevřená'}
            </Typography>
            {openPropustka && !!Number(openPropustka.needs_review) && (
              <Tooltip title="Tento záznam potřebuje kontrolu od nadřízeného">
                <IconWarningTriangle color="warning" style={{ fontSize: '1rem' }} />
              </Tooltip>
            )}
          </Stack>
        </Box>

        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          Co propustka je
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Propustka je potvrzení o dočasném opuštění pracoviště během směny na nezbytně nutnou dobu za účelem vyřízení osobních
          překážek v práci (nejčastěji lékařské vyšetření, doprovod rodinného příslušníka, úřední jednání, pohřeb či vlastní svatba).
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Upozornění: Zadání odchodu (propustky) v aplikaci slouží pouze k evidenci docházky.
        </Typography>
        <Typography variant="caption" sx={{ color: 'error.main' }}>
          Pro uznání překážky v práci a její proplacení je i nadále nutné doložit fyzické (či elektronické) potvrzení razítkem a
          podpisem lékaře (případně příslušného úřadu). Potvrzení odevzdejte nadřízenému.
        </Typography>

        <Button fullWidth size="small" variant="contained" onClick={openPropustkaModal} sx={{ textTransform: 'none', py: '8px' }}>
          {propustkaActionLabel}
        </Button>
        <Button fullWidth size="small" variant="outlined" onClick={closeModal} sx={{ textTransform: 'none', py: '8px' }}>
          Zavřít
        </Button>
      </ActionDialog>

      <ActionDialog open={openModal === 'propustka'} title="Propustka" onClose={closeModal}>
        {openPropustka ? (
          <Stack sx={{ gap: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Nevrácená propustka - {openPropustka.reason_label}
            </Typography>
            <TextField
              type="time"
              label="Čas návratu"
              value={returnTime}
              onChange={(e) => setReturnTime(e.target.value)}
              size="small"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <Button fullWidth size="small" variant="contained" disabled={returnBusy || !returnTime} onClick={handleMarkReturned} sx={{ textTransform: 'none', py: '8px' }}>
              {returnBusy ? <CircularProgress size={16} /> : 'Zaznamenat návrat'}
            </Button>
          </Stack>
        ) : (
          <>
            {myPropustkaRequests.filter((r) => r.status === 'schvaleno' && r.propustka_id === null).length > 0 && (
              <Stack sx={{ gap: 1, mb: 2 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Máte schválené žádanky - můžete je rovnou aktivovat do docházky:
                </Typography>
                {myPropustkaRequests
                  .filter((r) => r.status === 'schvaleno' && r.propustka_id === null)
                  .map((r) => {
                    const ReasonIcon = propustkaReasonIconFor(r.reason_icon);
                    return (
                      <Stack
                        key={r.id}
                        direction="row"
                        sx={{ gap: 1, alignItems: 'center', justifyContent: 'space-between', border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1.5, py: 1 }}
                      >
                        <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
                          {ReasonIcon && <ReasonIcon style={{ fontSize: '1.1rem' }} />}
                          <Stack>
                            <Typography variant="body2">{r.reason_label}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {formatDateDisplay(r.requested_date)}
                              {r.requested_departure_time ? ` · odchod ${r.requested_departure_time.slice(0, 5)}` : ''}
                              {r.requested_return_time ? ` · návrat ${r.requested_return_time.slice(0, 5)}` : ''}
                            </Typography>
                          </Stack>
                        </Stack>
                        <Button size="small" variant="contained" disabled={propustkaBusy} onClick={() => handleActivateRequest(r)} sx={{ textTransform: 'none' }}>
                          Aktivovat
                        </Button>
                      </Stack>
                    );
                  })}
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Nebo zapište novou propustku:
                </Typography>
              </Stack>
            )}
            <TextField
              select
              label="Důvod"
              required
              error={!propustkaReasonId}
              helperText={!propustkaReasonId ? 'Povinné pole' : ' '}
              value={propustkaReasonId}
              onChange={(e) => setPropustkaReasonId(e.target.value)}
              size="small"
              fullWidth
              sx={requiredFieldSx}
            >
              {propustkaReasons.map((r) => {
                const ReasonIcon = propustkaReasonIconFor(r.icon);
                return (
                  <MenuItem key={r.id} value={r.id}>
                    <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
                      {ReasonIcon && <ReasonIcon style={{ fontSize: '1.1rem' }} />}
                      <Typography variant="body2" sx={{ color: r.sort_order === 0 ? 'error.main' : undefined }}>
                        {r.label}
                      </Typography>
                    </Stack>
                  </MenuItem>
                );
              })}
            </TextField>
            {propustkaReasons.length === 0 && !propustkaError && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Zatím nejsou nastavené žádné důvody propustky - obraťte se na administrátora.
              </Typography>
            )}
            <TextField
              type="date"
              label="Datum"
              required
              error={!propustkaDate}
              helperText={!propustkaDate ? 'Povinné pole' : ' '}
              value={propustkaDate}
              onChange={(e) => setPropustkaDate(e.target.value)}
              size="small"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              sx={requiredFieldSx}
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={propustkaPending} onChange={(e) => setPropustkaPending(e.target.checked)} />}
              label={
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Nevím ještě, jestli půjde o propustku, nebo o pracovní neschopnost (rozhodne se dodatečně)
                </Typography>
              }
            />
            <Button
              fullWidth
              size="small"
              variant="contained"
              disabled={propustkaBusy || !propustkaReasonId || !propustkaDate}
              onClick={handleCreatePropustka}
              sx={{ textTransform: 'none', py: '8px' }}
            >
              {propustkaBusy ? <CircularProgress size={16} /> : 'Zapsat'}
            </Button>
          </>
        )}
        {propustkaError && (
          <Typography variant="body2" sx={{ color: 'error.main' }}>
            {propustkaError}
          </Typography>
        )}
        {!propustkaError && propustkaSuccess && (
          <Typography variant="body2" sx={{ color: 'success.main' }}>
            {propustkaSuccess}
          </Typography>
        )}
        <Button fullWidth size="small" variant="outlined" onClick={closeModal} sx={{ textTransform: 'none', py: '8px' }}>
          Zavřít
        </Button>
      </ActionDialog>

      <ActionDialog open={openModal === 'leave-info'} title="Žádost o dovolenou / n. volno" onClose={closeModal}>
        {/* Infotainment rámeček (2026-08-31, na výslovné přání uživatele) -
            stejný vzor jako u Propustky/OČR. Tady není "otevřená/zavřená"
            stav jako tam, takže rámeček místo toho ukazuje zbývající nárok
            (stejná hodnota, co dřív bývala v popisku accordionu). */}
        <Box
          component="fieldset"
          sx={{
            m: 0,
            px: 1.5,
            py: 1,
            border: '1px solid',
            borderColor: remainingVacationHours ? 'success.main' : 'divider',
            borderRadius: 1
          }}
        >
          <Typography
            component="legend"
            variant="caption"
            sx={{ px: 0.5, fontSize: '0.5625rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            Infotainment
          </Typography>
          <Typography variant="body2" sx={{ textAlign: 'center', color: remainingVacationHours ? 'success.main' : 'text.secondary' }}>
            {remainingVacationHours !== undefined && remainingVacationHours !== null
              ? `Zbývá ${remainingVacationHours} h dovolené`
              : 'Zbývající nárok se načítá...'}
          </Typography>
        </Box>

        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Žádost půjde ke schválení nadřízenému. Před odesláním si ověřte termín - po odeslání ji tady nejde upravit.
        </Typography>
        <Button fullWidth size="small" variant="contained" onClick={openLeaveModal} sx={{ textTransform: 'none', py: '8px' }}>
          Podat žádost
        </Button>
        <Button fullWidth size="small" variant="outlined" onClick={closeModal} sx={{ textTransform: 'none', py: '8px' }}>
          Zavřít
        </Button>
      </ActionDialog>

      <ActionDialog open={openModal === 'leave'} title="Žádost o dovolenou / n. volno" onClose={closeModal}>
        <TextField select label="Typ" value={leaveType} onChange={(e) => setLeaveType(e.target.value)} size="small" fullWidth>
          <MenuItem value="vacation">Dovolená</MenuItem>
          <MenuItem value="unpaid_leave">Neplacené volno</MenuItem>
        </TextField>
        <TextField
          type="date"
          label="Od"
          required
          error={!leaveStart}
          helperText={!leaveStart ? 'Povinné pole' : ' '}
          value={leaveStart}
          onChange={(e) => setLeaveStart(e.target.value)}
          size="small"
          fullWidth
          slotProps={{ inputLabel: { shrink: true } }}
          sx={requiredFieldSx}
        />
        <TextField
          type="date"
          label="Do"
          required
          error={!leaveEnd}
          helperText={!leaveEnd ? 'Povinné pole' : ' '}
          value={leaveEnd}
          onChange={(e) => setLeaveEnd(e.target.value)}
          size="small"
          fullWidth
          slotProps={{ inputLabel: { shrink: true } }}
          sx={requiredFieldSx}
        />
        {leaveType === 'vacation' && (
          <>
            <Stack direction="row" sx={{ gap: 1.5 }}>
              <TextField
                type="number"
                label="Počet dní"
                value={leaveDays}
                onChange={(e) => handleLeaveDaysChange(e.target.value)}
                size="small"
                fullWidth
                placeholder="1"
                slotProps={{ htmlInput: { min: 0.0625, step: 0.0625 }, inputLabel: { shrink: true } }}
              />
              <TextField
                type="number"
                label="Počet hodin"
                value={leaveHours}
                onChange={(e) => handleLeaveHoursChange(e.target.value)}
                size="small"
                fullWidth
                placeholder="8"
                slotProps={{ htmlInput: { min: 0.5, step: 0.5 }, inputLabel: { shrink: true } }}
              />
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Zadejte dny NEBO hodiny - druhé pole se přepočítá samo (1 den = {VACATION_HOURS_PER_DAY} h). Appka nerozlišuje celý/půl
              den, ukládá se vždy jen počet hodin.
            </Typography>
            {leaveHours && remainingVacationHours !== undefined && remainingVacationHours !== null && (
              <Typography
                variant="caption"
                sx={{ color: remainingVacationHours - Number(leaveHours) < 0 ? 'error.main' : 'text.secondary' }}
              >
                Odečte se {leaveHours} h. Po schválení zbyde {(remainingVacationHours - Number(leaveHours)).toFixed(2)} h
                {remainingVacationHours - Number(leaveHours) < 0 ? ' - jde do záporného stavu (přečerpání)' : ''}.
              </Typography>
            )}
          </>
        )}
        <Button
          fullWidth
          size="small"
          variant="contained"
          disabled={leaveBusy || !leaveStart || !leaveEnd || (leaveType === 'vacation' && !leaveHours)}
          onClick={handleRequestLeave}
          sx={{ textTransform: 'none', py: '8px' }}
        >
          {leaveBusy ? <CircularProgress size={16} /> : 'Podat žádost'}
        </Button>
        {leaveError && (
          <Typography variant="body2" sx={{ color: 'error.main' }}>
            {leaveError}
          </Typography>
        )}
        {!leaveError && leaveSuccess && (
          <Typography variant="body2" sx={{ color: 'success.main' }}>
            {leaveSuccess}
          </Typography>
        )}
        <Button fullWidth size="small" variant="outlined" onClick={closeModal} sx={{ textTransform: 'none', py: '8px' }}>
          Zavřít
        </Button>
      </ActionDialog>

      <ActionDialog open={openModal === 'ocr-info'} title="Ošetřování člena rodiny" onClose={closeModal}>
        {/* Infotainment rámeček (2026-08-31, na výslovné přání uživatele) -
            stejný vzor jako u Propustky, barva rámečku i textu podle stavu
            (otevřená OČR = success, zavřená = neutrální divider/secondary). */}
        <Box
          component="fieldset"
          sx={{ m: 0, px: 1.5, py: 1, border: '1px solid', borderColor: openOcr ? 'success.main' : 'divider', borderRadius: 1 }}
        >
          <Typography
            component="legend"
            variant="caption"
            sx={{ px: 0.5, fontSize: '0.5625rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            Infotainment
          </Typography>
          <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" sx={{ color: openOcr ? 'success.main' : 'text.secondary' }}>
              {openOcr ? `Otevřená OČR od ${formatDateDisplay(openOcr.start_date)}` : 'Zatím žádná otevřená OČR.'}
            </Typography>
            {openOcr && !!Number(openOcr.needs_review) && (
              <Tooltip title="Tento záznam potřebuje kontrolu od nadřízeného">
                <IconWarningTriangle color="warning" style={{ fontSize: '1rem' }} />
              </Tooltip>
            )}
          </Stack>
        </Box>

        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          SMS kód s identifikátorem pro OČR nedostanete pokaždé - podmínkou je, že své telefonní číslo dobrovolně nahlásíte
          ošetřujícímu lékaři nebo sestře a budete trvat na elektronické podobě.
        </Typography>
        <Typography variant="caption" sx={{ color: 'error.main' }}>
          Podle ČSSZ se kontakt předává při každém jednotlivém podání zvlášť (na nástup na OČR), zároveň (na ukončení OČR) a slouží
          jen pro jednorázovou notifikaci k danému úkonu - je proto potřeba
          číslo nahlásit znovu i při ukončení OČR, nestačí ho nahlásit jen na začátku.
        </Typography>
        <Button fullWidth size="small" variant="contained" onClick={openOcrModal} sx={{ textTransform: 'none', py: '8px' }}>
          {openOcr ? 'Ukončit OČR' : 'Zaznamenat OČR'}
        </Button>
        <Button fullWidth size="small" variant="outlined" onClick={closeModal} sx={{ textTransform: 'none', py: '8px' }}>
          Zavřít
        </Button>
      </ActionDialog>

      <ActionDialog open={openModal === 'ocr'} title="OČR" onClose={closeModal}>
        {openOcr ? (
          <Stack sx={{ gap: 1 }}>
            <Typography variant="caption" sx={{ color: 'success.main' }}>
              Otevřená OČR od {formatDateDisplay(openOcr.start_date)}
            </Typography>
            <TextField
              type="date"
              label="Datum ukončení"
              required
              error={!ocrEndDate}
              helperText={!ocrEndDate ? 'Povinné pole' : ' '}
              sx={requiredFieldSx}
              value={ocrEndDate}
              onChange={(e) => setOcrEndDate(e.target.value)}
              size="small"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Ukončovací SMS kód"
              required
              error={!ocrEndCareCode.trim()}
              helperText={!ocrEndCareCode.trim() ? 'Povinné pole' : ' '}
              value={ocrEndCareCode}
              onChange={(e) => setOcrEndCareCode(e.target.value)}
              size="small"
              fullWidth
              sx={requiredFieldSx}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Ukončovací kód je SAMOSTATNÁ žádost u lékaře, ne ten stejný jako na začátku - i tady musíte znovu požádat o elektronické
              podání.{' '}
              <Typography
                component="span"
                variant="caption"
                onClick={() => goTo('/attendance/dashboard/employee-documentation')}
                sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
              >
                Nemáte ukončovací kód?
              </Typography>
            </Typography>
            <Button
              fullWidth
              size="small"
              variant="contained"
              disabled={ocrBusy || !ocrEndDate || !ocrEndCareCode.trim()}
              onClick={handleCloseOcr}
              sx={{ textTransform: 'none', py: '8px' }}
            >
              {ocrBusy ? <CircularProgress size={16} /> : 'Ukončit'}
            </Button>
          </Stack>
        ) : (
          <>
            <TextField
              type="date"
              label="Od"
              required
              error={!ocrStart}
              helperText={!ocrStart ? 'Povinné pole' : ' '}
              value={ocrStart}
              onChange={(e) => setOcrStart(e.target.value)}
              size="small"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              sx={requiredFieldSx}
            />
            <TextField
              label="SMS kód od lékaře"
              required
              error={!ocrCareCode.trim()}
              helperText={!ocrCareCode.trim() ? 'Povinné pole' : ' '}
              value={ocrCareCode}
              onChange={(e) => setOcrCareCode(e.target.value)}
              size="small"
              fullWidth
              sx={requiredFieldSx}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Kód dostanete jen u ELEKTRONICKY podané žádosti (nahlaste telefon lékaři/sestře).{' '}
              <Typography
                component="span"
                variant="caption"
                onClick={() => goTo('/attendance/dashboard/employee-documentation')}
                sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
              >
                Nemáte kód / mám jen papír?
              </Typography>
            </Typography>
            <Button
              fullWidth
              size="small"
              variant="contained"
              disabled={ocrBusy || !ocrStart || !ocrCareCode.trim()}
              onClick={handleRecordOcr}
              sx={{ textTransform: 'none', py: '8px' }}
            >
              {ocrBusy ? <CircularProgress size={16} /> : 'Zaznamenat'}
            </Button>
          </>
        )}
        {ocrError && (
          <Typography variant="body2" sx={{ color: 'error.main' }}>
            {ocrError}
          </Typography>
        )}
        {!ocrError && ocrSuccess && (
          <Typography variant="body2" sx={{ color: 'success.main' }}>
            {ocrSuccess}
          </Typography>
        )}
        <Button fullWidth size="small" variant="outlined" onClick={closeModal} sx={{ textTransform: 'none', py: '8px' }}>
          Zavřít
        </Button>
      </ActionDialog>
      </Accordion>

      {/* "Docházka" a "Žádosti o nepřítomnost" (2026-08-29, na výslovné
          přání uživatele - "nechtěl jsem to do accordion Docházka a
          žádosti, chtěl jsem to pod accordion jako odkazy") - vždy viditelné
          odkazy PŘÍMO POD celým accordionem, ne uvnitř jeho rozbalovacího
          obsahu. `onNavigate` (viz Profile/index.jsx) zavře offcanvas při
          navigaci, aby uživatel neskončil s otevřeným panelem nad cílovou
          stránkou.
          "Docházkové karty" (2026-09-01, na výslovné přání uživatele) odsud
          zmizelo - přesunuto do left-menu "Manažeři" (menu-items/attendance.jsx),
          stránka je teď manažerská (regional_manager+, viz MainRoutes.jsx). */}
      <List sx={{ p: 0 }}>
        <ListItemButton onClick={() => goTo('/attendance/activity')} sx={{ px: 2.5, borderBottom: 1, borderColor: 'divider' }}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <IconFactCheck style={{ fontSize: '1.3rem' }} />
          </ListItemIcon>
          <ListItemText primary="Záznam docházky" />
        </ListItemButton>
        <ListItemButton onClick={() => goTo('/attendance/leave-requests')} sx={{ px: 2.5, borderBottom: 1, borderColor: 'divider' }}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <IconWorkHistory style={{ fontSize: '1.3rem' }} />
          </ListItemIcon>
          <ListItemText primary="Moje žádosti" />
        </ListItemButton>
      </List>
    </>
  );
}
